import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Download, Trash2, Paperclip, FileText, Image, File, Mail, Info } from 'lucide-react';
import OsScroll from '../ui/OsScroll';
import { appDateLocale } from '../../utils/appLocale';
import { formatBytes } from '../../utils/formatBytes';
import { saveAttachment, saveRaw } from '../../services/mailCatcherService';
import { DETAIL_TABS } from '../../store/mailCatcherStore';
import MailHtmlPreview from './MailHtmlPreview';
import { senderName, senderAddress } from './mailAvatar';
import SpamPill from './SpamPill';
import SpamReasonsModal from './SpamReasonsModal';
import { normalizeSpam } from './spamRules';

/** Full date for the reading pane — app locale, never the OS one (`utils/appLocale`). */
export function formatFullDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString(appDateLocale(), { dateStyle: 'medium', timeStyle: 'short' });
}

function iconForMime(mime) {
  const m = String(mime || '');
  if (m.startsWith('image/')) return Image;
  if (m.startsWith('text/') || m === 'application/pdf' || m === 'application/json') return FileText;
  return File;
}

function safeFilename(subject) {
  const base = String(subject || 'message')
    .replace(/[^\w.-]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60);
  return `${base || 'message'}.eml`;
}

/** Recipients inline in the meta line: every address its own mono span. */
function Recipients({ items, prefix }) {
  if (!items || items.length === 0) return null;
  return (
    <span className="mailc-meta-recipients">
      {prefix && <span className="mailc-meta-key">{prefix}</span>}
      {items.map((a, i) => (
        <span key={`${a}-${i}`} className="mailc-meta-addr" title={a}>
          {a}
        </span>
      ))}
    </span>
  );
}

/**
 * The reading pane, mail-client shape (owner mockup 08.09.2026):
 *   Subject                                             [⤓] [🗑]
 *   Dev dev@example.test → qa@example.test · Sep 8, 8:53 PM · 4.2 KB
 *   [HTML] Text Headers Raw                 ⓘ Links and remote images disabled
 *   ┌─ rounded white preview card ─┐
 * The store decides the initial tab (HTML when the message has an HTML part).
 */
export default function MailCatcherDetail({ detail, tab, raw, loading = false, onTab, onDelete }) {
  const { t } = useTranslation();
  const [notice, setNotice] = useState(null);
  // The spam reasons modal — closes when the selection moves on.
  const [spamOpen, setSpamOpen] = useState(false);

  useEffect(() => {
    setNotice(null);
    setSpamOpen(false);
  }, [detail?.summary?.id]);

  if (!detail) {
    return (
      <div className="mailc-detail mailc-detail--empty" data-testid="mail-no-selection">
        <div className="mailc-list-empty-icon">
          <Mail size={22} />
        </div>
        <span className="mailc-list-empty-title">{t('mailCatcher.noSelection')}</span>
      </div>
    );
  }

  const { summary } = detail;
  const id = summary.id;
  const subject = summary.subject || t('mailCatcher.noSubject');
  const from = detail.from || summary.from;
  const date = formatFullDate(detail.date || summary.receivedAt);
  const size = summary.size != null ? formatBytes(summary.size) : '';
  const headers = detail.headers || [];
  const spam = normalizeSpam(detail);

  const report = (p) => {
    p.then((r) => {
      if (r?.saved) setNotice({ ok: true, text: t('mailCatcher.saved', { path: r.path }) });
    }).catch((e) => setNotice({ ok: false, text: t('mailCatcher.saveFailed', { error: (e && e.message) || String(e) }) }));
  };

  const tabLabel = { html: 'tabHtml', text: 'tabText', headers: 'tabHeaders', raw: 'tabRaw' };

  return (
    // While the next message is being fetched the previous one stays on
    // screen (see mailCatcherStore.select); `--switching` dims the body after
    // a short CSS delay so a fast local fetch shows nothing at all.
    <div className={`mailc-detail${loading ? ' mailc-detail--switching' : ''}`} data-testid="mail-detail" data-loading={loading ? 'true' : 'false'}>
      <div className="mailc-detail-head">
        <div className="mailc-detail-title-row">
          <h2 className="mailc-detail-subject">{subject}</h2>
          <div className="mailc-detail-actions">
            <SpamPill score={spam.score} threshold={spam.threshold} onOpen={() => setSpamOpen(true)} />
            <button
              type="button"
              className="mailc-iconbtn"
              onClick={() => report(saveRaw(id, safeFilename(summary.subject)))}
              aria-label={t('mailCatcher.downloadRaw')}
              title={t('mailCatcher.downloadRaw')}
            >
              <Download size={15} />
            </button>
            <button
              type="button"
              className="mailc-iconbtn mailc-iconbtn--danger"
              onClick={() => onDelete(id)}
              aria-label={t('mailCatcher.delete')}
              title={t('mailCatcher.delete')}
            >
              <Trash2 size={15} />
            </button>
          </div>
        </div>

        <div className="mailc-meta" data-testid="mail-meta">
          <span className="mailc-meta-sender">{senderName(from)}</span>
          {senderAddress(from) && <span className="mailc-meta-addr mailc-meta-addr--from">{senderAddress(from)}</span>}
          {detail.to && detail.to.length > 0 && <ArrowRight size={12} className="mailc-meta-arrow" aria-hidden="true" />}
          <Recipients items={detail.to} />
          <Recipients items={detail.cc} prefix={t('mailCatcher.cc')} />
          {date && (
            <>
              <span className="mailc-meta-sep">·</span>
              <span className="mailc-meta-date" title={t('mailCatcher.date')}>
                {date}
              </span>
            </>
          )}
          {size && (
            <>
              <span className="mailc-meta-sep">·</span>
              <span className="mailc-meta-size">{size}</span>
            </>
          )}
        </div>

        {detail.attachments && detail.attachments.length > 0 && (
          <div className="mailc-attachments" data-testid="mail-attachments">
            <span className="mailc-hdr-key">
              <Paperclip size={12} /> {t('mailCatcher.attachments')}
            </span>
            {detail.attachments.map((a) => {
              const Icon = iconForMime(a.mime);
              return (
                <span key={a.index} className="mailc-attachment" data-testid="mail-attachment">
                  <Icon size={13} className="mailc-attachment-icon" />
                  <span className="mailc-attachment-name" title={a.filename}>
                    {a.filename}
                  </span>
                  <span className="mailc-attachment-size">{formatBytes(a.size)}</span>
                  <button
                    type="button"
                    className="mailc-attachment-dl"
                    aria-label={`${t('mailCatcher.download')} ${a.filename}`}
                    title={t('mailCatcher.download')}
                    onClick={() => report(saveAttachment(id, a.index, a.filename))}
                  >
                    <Download size={12} />
                  </button>
                </span>
              );
            })}
          </div>
        )}

        {notice && (
          <div className={`mailc-notice${notice.ok ? '' : ' mailc-notice--error'}`} role="status">
            {notice.text}
          </div>
        )}

        <div className="mailc-tabs-row">
          <div className="mailc-tabs" role="tablist">
            {DETAIL_TABS.map((key) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={tab === key}
                className={`mailc-tab${tab === key ? ' mailc-tab--active' : ''}`}
                onClick={() => onTab(key)}
              >
                {t(`mailCatcher.${tabLabel[key]}`)}
              </button>
            ))}
          </div>
          {tab === 'html' && (
            <span className="mailc-preview-note" data-testid="mail-preview-note">
              <Info size={13} aria-hidden="true" />
              {t('mailCatcher.previewLinksInert')}
            </span>
          )}
        </div>
      </div>

      <div className="mailc-detail-body">
        {tab === 'html' &&
          (typeof detail.html === 'string' ? (
            <MailHtmlPreview html={detail.html} />
          ) : (
            <div className="mailc-body-empty">{t('mailCatcher.noHtml')}</div>
          ))}
        {tab === 'text' && (
          <OsScroll className="mailc-body-scroll mailc-body-card">
            {typeof detail.text === 'string' ? (
              <pre className="mailc-pre" data-testid="mail-text">
                {detail.text}
              </pre>
            ) : (
              <div className="mailc-body-empty">{t('mailCatcher.noText')}</div>
            )}
          </OsScroll>
        )}
        {tab === 'headers' && (
          <OsScroll className="mailc-body-scroll mailc-body-card">
            {/* The SAME table as Collections → Response → Headers
                (ResponseViewer.renderHeadersTab, `rv-json-*` in index.css):
                a mail header row and a response header row are the same kind
                of object, so they share one look. */}
            <div className="mailc-headers-wrap">
              <div className="rv-headers-summary">
                {t('response.headersCount', {
                  count: headers.length,
                  bytes: headers.reduce((n, [k, v]) => n + String(k).length + String(v).length, 0),
                })}
              </div>
              <div className="rv-json-table-wrap">
                <table className="rv-json-table" data-testid="mail-headers">
                  <thead>
                    <tr>
                      <th className="rv-json-th">{t('request.key')}</th>
                      <th className="rv-json-th">{t('request.value')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {headers.map(([k, v], i) => (
                      <tr key={`${k}-${i}`} className="rv-json-tr">
                        <td className="rv-json-td rv-json-td--key">{k}</td>
                        <td className="rv-json-td">{String(v)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </OsScroll>
        )}
        {tab === 'raw' && (
          <OsScroll className="mailc-body-scroll mailc-body-card">
            <pre className="mailc-pre mailc-pre--raw" data-testid="mail-raw">
              {raw ?? ''}
            </pre>
          </OsScroll>
        )}
      </div>
      <SpamReasonsModal isOpen={spamOpen} onClose={() => setSpamOpen(false)} spam={spam} />
    </div>
  );
}
